import React, { useState, useEffect } from 'react';
import { getThemes, createTheme, updateTheme, deleteTheme, reorderThemes } from '../../api/adminApi';
import Modal from '../../components/admin/Modal';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable row component
function SortableThemeRow({ theme, onEdit, onDelete }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: theme.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    borderBottom: '1px solid #dee2e6',
    backgroundColor: isDragging ? '#f8f9fa' : 'white',
  };

  return (
    <tr ref={setNodeRef} style={style}>
      <td
        style={{
          padding: '1rem',
          width: '40px',
          cursor: 'grab',
          userSelect: 'none'
        }}
        {...attributes}
        {...listeners}
      >
        <span style={{ fontSize: '1.2rem', color: '#95a5a6' }}>⋮⋮</span>
      </td>
      <td style={{ padding: '1rem', fontWeight: 500 }}>{theme.name}</td>
      <td style={{ padding: '1rem', color: '#7f8c8d' }}>{theme.slug}</td>
      <td style={{ padding: '1rem', textAlign: 'right' }}>
        <button
          onClick={() => onEdit(theme)}
          style={{
            padding: '0.5rem 1rem',
            marginRight: '0.5rem',
            backgroundColor: '#3498db',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Éditer
        </button>
        <button
          onClick={() => onDelete(theme)}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#e74c3c',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Supprimer
        </button>
      </td>
    </tr>
  );
}

function ThemesPage() {
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTheme, setEditingTheme] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, theme: null });
  const [isReordering, setIsReordering] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    slug: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchThemes();
  }, []);

  const fetchThemes = async () => {
    try {
      setLoading(true);
      const data = await getThemes();
      setThemes(data);
    } catch (err) {
      console.error('Error fetching themes:', err);
      alert('Erreur de chargement des thèmes');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingTheme(null);
    setFormData({
      name: '',
      slug: ''
    });
    setIsModalOpen(true);
  };

  const openEditModal = (theme) => {
    setEditingTheme(theme);
    setFormData({
      name: theme.name,
      slug: theme.slug
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingTheme(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-generate slug from name
    if (name === 'name' && !editingTheme) {
      const slug = value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setFormData(prev => ({ ...prev, slug }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (editingTheme) {
        await updateTheme(editingTheme.id, formData);
      } else {
        await createTheme(formData);
      }

      await fetchThemes();
      closeModal();
    } catch (err) {
      console.error('Error saving theme:', err);
      alert('Erreur lors de l\'enregistrement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (theme) => {
    setDeleteDialog({ isOpen: true, theme });
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteTheme(deleteDialog.theme.id);
      setThemes(themes.filter(t => t.id !== deleteDialog.theme.id));
      setDeleteDialog({ isOpen: false, theme: null });
    } catch (err) {
      console.error('Error deleting theme:', err);
      alert('Erreur lors de la suppression');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over) return;

    // Prevent concurrent reorder operations
    if (isReordering) return;

    if (active.id !== over.id) {
      const oldIndex = themes.findIndex((theme) => theme.id === active.id);
      const newIndex = themes.findIndex((theme) => theme.id === over.id);

      // Validate indices
      if (oldIndex === -1 || newIndex === -1) {
        console.error('Invalid drag indices');
        return;
      }

      // Save previous state for immediate rollback
      const previousThemes = [...themes];
      const newThemes = arrayMove(themes, oldIndex, newIndex);

      // Update local state immediately for smooth UX
      setThemes(newThemes);
      setIsReordering(true);

      // Update display_order for all themes and save to backend
      const updatedThemes = newThemes.map((theme, index) => ({
        id: theme.id,
        display_order: index
      }));

      try {
        await reorderThemes(updatedThemes);
      } catch (err) {
        console.error('Error reordering themes:', err);
        alert('Erreur lors de la réorganisation. Les modifications ont été annulées.');
        // Immediate rollback to previous state
        setThemes(previousThemes);
      } finally {
        // Always reset the flag, even if error occurred
        setIsReordering(false);
      }
    }
  };

  if (loading) {
    return <div>Chargement...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h1 style={{ color: '#2c3e50' }}>Thèmes ({themes.length})</h1>
        <button
          onClick={openCreateModal}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#9c6f44',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 600
          }}
        >
          + Nouveau thème
        </button>
      </div>

      <div style={{ marginBottom: '1rem', color: '#7f8c8d', fontSize: '0.9rem' }}>
        💡 Astuce : Glissez-déposez les lignes pour réorganiser l'ordre des thèmes
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '1rem', width: '40px' }}></th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Nom</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Slug</th>
                <th style={{ padding: '1rem', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <SortableContext
              items={themes.map(t => t.id)}
              strategy={verticalListSortingStrategy}
            >
              <tbody>
                {themes.length === 0 ? (
                  <tr>
                    <td colSpan="4" style={{ padding: '2rem', textAlign: 'center', color: '#7f8c8d' }}>
                      Aucun thème trouvé
                    </td>
                  </tr>
                ) : (
                  themes.map(theme => (
                    <SortableThemeRow
                      key={theme.id}
                      theme={theme}
                      onEdit={openEditModal}
                      onDelete={handleDeleteClick}
                    />
                  ))
                )}
              </tbody>
            </SortableContext>
          </table>
        </div>
      </DndContext>

      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingTheme ? 'Modifier le thème' : 'Nouveau thème'}
      >
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              Nom *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
              Slug *
            </label>
            <input
              type="text"
              name="slug"
              value={formData.slug}
              onChange={handleInputChange}
              required
              pattern="[a-z0-9-]+"
              title="Lettres minuscules, chiffres et tirets uniquement"
              style={{
                width: '100%',
                padding: '0.5rem',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
            <small style={{ color: '#7f8c8d' }}>Lettres minuscules, chiffres et tirets uniquement</small>
          </div>

          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
            <button
              type="button"
              onClick={closeModal}
              style={{
                padding: '0.5rem 1.5rem',
                backgroundColor: '#95a5a6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={submitting}
              style={{
                padding: '0.5rem 1.5rem',
                backgroundColor: '#9c6f44',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.7 : 1
              }}
            >
              {submitting ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialog({ isOpen: false, theme: null })}
        title="Confirmer la suppression"
        message={`Êtes-vous sûr de vouloir supprimer le thème "${deleteDialog.theme?.name}" ? Tous les produits associés seront également supprimés.`}
      />
    </div>
  );
}

export default ThemesPage;
